export function TermsOfService() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="mb-8 text-3xl font-bold tracking-tight">Terms and Conditions</h1>
      <p className="mb-4 text-sm text-gray-500">Effective Date: June 2026</p>

      <section className="space-y-6 text-gray-700 dark:text-gray-300 [&_h2]:mb-2 [&_h2]:mt-8 [&_h2]:text-xl [&_h2]:font-semibold [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-1">
        <p>
          These Terms and Conditions govern your use of the BidConvey platform.
          By accessing or using BidConvey, you confirm that you have read,
          understood and agree to be bound by these Terms and Conditions.
        </p>

        <h2>1. Definitions</h2>
        <p>For the purposes of these Terms:</p>
        <ul>
          <li>
            <strong>BidConvey</strong> means the online platform that facilitates
            the selection of a transferring conveyancer by property buyers and
            sellers;
          </li>
          <li>
            <strong>Buyer</strong> means the person purchasing the immovable
            property;
          </li>
          <li>
            <strong>Seller</strong> means the person selling the immovable
            property;
          </li>
          <li>
            <strong>Conveyancer</strong> means an admitted attorney authorised to
            perform conveyancing services in South Africa;
          </li>
          <li>
            <strong>Standard Conveyancing Tariff Fee</strong> means the benchmark
            conveyancing fee determined by the Legal Practice Council&rsquo;s
            tariff for the relevant transaction, published and amended from time
            to time;
          </li>
          <li>
            <strong>Saving</strong> means the difference between the Standard
            Conveyancing Tariff Fee and the quotation accepted by the buyer
            through the BidConvey platform.
          </li>
        </ul>

        <h2>2. Purpose of BidConvey</h2>
        <p>
          BidConvey is an online conveyancer selection platform that enables
          buyers and sellers to participate in the selection of a transferring
          conveyancer through a transparent quotation process.
        </p>
        <p>
          BidConvey is a technology platform only. It is not a law firm,
          conveyancing practice, estate agency or financial adviser and does not
          provide legal, financial or tax advice.
        </p>

        <h2>3. Eligibility</h2>
        <p>You may only use BidConvey if you:</p>
        <ul>
          <li>are at least 18 years old;</li>
          <li>have the legal capacity to enter into legally binding agreements; and</li>
          <li>provide complete, accurate and truthful information.</li>
        </ul>

        <h2>4. Conveyancer Selection</h2>
        <p>BidConvey facilitates the conveyancer selection process only.</p>
        <p>The appointment of a conveyancer remains subject to:</p>
        <ul>
          <li>the applicable Offer to Purchase or Sale Agreement;</li>
          <li>acceptance of the instruction by the selected conveyancer; and</li>
          <li>applicable South African law.</li>
        </ul>
        <p>
          BidConvey does not guarantee that any property transaction will proceed
          to registration.
        </p>

        <h2>5. BidConvey Service Fee</h2>
        <p>
          The BidConvey service fee is 10% of the Saving achieved through the
          BidConvey platform.
        </p>
        <p>The Saving is calculated as the difference between:</p>
        <ul>
          <li>the Standard Conveyancing Tariff Fee; and</li>
          <li>the quotation accepted by the buyer through the BidConvey platform.</li>
        </ul>
        <p>
          The BidConvey service fee only applies to transfer fees and is separate
          from:
        </p>
        <ul>
          <li>VAT applicable to the selling price;</li>
          <li>transfer duty applied to the selling price;</li>
          <li>deeds office fees;</li>
          <li>rates clearance costs;</li>
          <li>bank charges;</li>
          <li>FICA costs; and</li>
          <li>any other disbursements or third-party charges.</li>
        </ul>
        <p>
          The applicable BidConvey service fee will be disclosed to the user
          before payment is required.
        </p>

        <h2>6. Payment</h2>
        <p>
          The BidConvey service fee becomes immediately due and payable prior to
          release by BidConvey of the selected conveyancer&rsquo;s name and
          contact details to the buyer and/or seller.
        </p>
        <p>
          The release of the selected conveyancer&rsquo;s details constitutes
          full performance of BidConvey&rsquo;s service.
        </p>

        <h2>7. Refund Policy</h2>
        <p>
          Because BidConvey&rsquo;s service is fully performed once the selected
          conveyancer&rsquo;s details have been released, all BidConvey service
          fees are final and non-refundable.
        </p>
        <p>No refund shall be payable if:</p>
        <ul>
          <li>the property transaction is cancelled;</li>
          <li>the Offer to Purchase lapses or is terminated;</li>
          <li>the buyer or seller decides not to proceed;</li>
          <li>the selected conveyancer is not ultimately appointed;</li>
          <li>another conveyancer is subsequently appointed;</li>
          <li>transfer is delayed or never registered; or</li>
          <li>
            any dispute, event or circumstance beyond BidConvey&rsquo;s
            reasonable control affects the transaction.
          </li>
        </ul>

        <h2>8. User Responsibilities</h2>
        <p>Users agree to:</p>
        <ul>
          <li>provide accurate and complete information;</li>
          <li>use the platform honestly, lawfully and in good faith;</li>
          <li>maintain the confidentiality of their login credentials;</li>
          <li>
            not misuse, interfere with or attempt to circumvent the operation of
            the platform; and
          </li>
          <li>comply with all applicable laws.</li>
        </ul>

        <h2>9. Quotations</h2>
        <p>All quotations are submitted by independent conveyancing firms.</p>
        <p>BidConvey does not warrant or guarantee:</p>
        <ul>
          <li>the lowest quotation;</li>
          <li>the quality of legal services;</li>
          <li>turnaround times;</li>
          <li>successful transfer of the property; or</li>
          <li>any particular outcome.</li>
        </ul>
        <p>
          Users remain responsible for evaluating quotations and selecting the
          conveyancer they consider most suitable.
        </p>

        <h2>10. Limitation of Liability</h2>
        <p>
          BidConvey acts solely as a technology platform facilitating the
          conveyancer selection process.
        </p>
        <p>
          To the fullest extent permitted by law, BidConvey shall not be liable
          for any direct, indirect, incidental or consequential loss, damage,
          delay, cost or expense arising from:
        </p>
        <ul>
          <li>the services provided by any conveyancer;</li>
          <li>the property transaction;</li>
          <li>cancellation or failure of the sale;</li>
          <li>delays in registration;</li>
          <li>
            errors or omissions by buyers, sellers, estate agents, mortgage
            originators, financial institutions, municipalities or conveyancers;
            or
          </li>
          <li>any matter beyond BidConvey&rsquo;s reasonable control.</li>
        </ul>

        <h2>11. Privacy</h2>
        <p>
          BidConvey collects, stores and processes personal information in
          accordance with the Protection of Personal Information Act, 2013
          (POPIA). By using the platform, you consent to the collection and
          processing of your personal information for purposes connected with the
          provision of BidConvey&rsquo;s services.
        </p>

        <h2>12. Intellectual Property</h2>
        <p>
          All software, branding, logos, content, graphics, trademarks and
          intellectual property on the BidConvey platform remain the exclusive
          property of BidConvey. No content may be copied, reproduced,
          distributed or used without BidConvey&rsquo;s prior written consent.
        </p>

        <h2>13. Suspension or Termination</h2>
        <p>
          BidConvey reserves the right to suspend or terminate access to the
          platform where:
        </p>
        <ul>
          <li>these Terms are breached;</li>
          <li>fraudulent or unlawful activity is suspected;</li>
          <li>false information is provided; or</li>
          <li>continued access may prejudice BidConvey or other users.</li>
        </ul>

        <h2>14. Amendments</h2>
        <p>
          BidConvey may amend these Terms and Conditions at any time. Updated
          Terms will become effective upon publication on the BidConvey website.
          Continued use of the platform constitutes acceptance of the revised
          Terms.
        </p>

        <h2>15. Governing Law</h2>
        <p>
          These Terms and Conditions are governed by the laws of the Republic of
          South Africa. Any dispute arising from these Terms shall be subject to
          the jurisdiction of the courts of South Africa.
        </p>

        <h2>16. Contact</h2>
        <p>
          If you have any questions regarding these Terms and Conditions, please
          contact:<br />
          BidConvey<br />
          Email:{" "}
          <a href="mailto:info@bidconvey.com" className="underline font-medium">
            info@bidconvey.com
          </a>
          <br />
          Website:{" "}
          <a href="https://www.bidconvey.com" className="underline font-medium">
            www.bidconvey.com
          </a>
        </p>

        <h2>Acceptance</h2>
        <p>
          By registering for, accessing or using the BidConvey platform, and by
          proceeding with the conveyancer selection process, you acknowledge that
          you have read, understood and agree to be legally bound by these Terms
          and Conditions.
        </p>
      </section>

      <div className="mt-12">
        <a href="/" className="text-sm font-medium text-blue-600 hover:underline">
          &larr; Back to home
        </a>
      </div>
    </div>
  );
}
